import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { Field, Form, Formik } from 'formik';
import { COLLABORATOR_SECTION } from '../../doppler-types';
import { InjectAppServices } from '../../services/pure-di';
import { isCollaboratorAccountReportEnabled } from '../../services/feature-flags';
import { FormMessages, SubmitButton } from '../form-helpers/form-helpers';
import { GoBackButton } from '../BuyProcess/PlanSelection/GoBackButton';
import HeaderSection from '../shared/HeaderSection/HeaderSection';
import { Breadcrumb, BreadcrumbItem } from '../shared/Breadcrumb/Breadcrumb';
import { CollapsibleSection } from '../shared/CollapsibleSection/CollapsibleSection';

const BROWSER_NOTIFICATION_PERMISSION = {
  DEFAULT: 'default',
  GRANTED: 'granted',
  DENIED: 'denied',
};

const getBrowserNotificationPermission = () => Notification.permission;

const canUseBrowserNotifications = (permission) =>
  permission === BROWSER_NOTIFICATION_PERMISSION.GRANTED;

const canEnableConversationNotifications = async () => {
  if (Notification.permission === BROWSER_NOTIFICATION_PERMISSION.GRANTED) {
    return true;
  }

  if (Notification.permission === BROWSER_NOTIFICATION_PERMISSION.DENIED) {
    return false;
  }

  if (Notification.permission !== BROWSER_NOTIFICATION_PERMISSION.DEFAULT) {
    return false;
  }

  try {
    return (await Notification.requestPermission()) === BROWSER_NOTIFICATION_PERMISSION.GRANTED;
  } catch {
    return false;
  }
};

export const AccountNotifications = InjectAppServices((props) => {
  const { appSessionRef, dopplerUserApiClient, sessionManager } = props.dependencies;
  const intl = useIntl();
  const userData = appSessionRef.current.userData;
  const userAccount = userData?.userAccount;
  const ownerEmail = userData?.user?.email || '';
  const isCollaborator = userAccount?.userProfileType === 'COLLABORATOR';
  const hasConversationsAccess = userAccount?.collaboratorViewAccessRights?.some(
    ({ idSection }) => idSection === COLLABORATOR_SECTION.Conversations,
  );
  const showConversations = !isCollaborator || hasConversationsAccess;
  const showAccountActivity = isCollaboratorAccountReportEnabled();
  const [isAccountActivityOpen, setIsAccountActivityOpen] = useState(false);
  const [isConversationsOpen, setIsConversationsOpen] = useState(false);
  const [accountNotificationSettings, setAccountNotificationSettings] = useState(null);
  const [browserNotificationPermission, setBrowserNotificationPermission] = useState(
    getBrowserNotificationPermission,
  );
  const areSettingsLoaded = accountNotificationSettings !== null;

  useEffect(() => {
    const syncPermission = () => {
      setBrowserNotificationPermission(getBrowserNotificationPermission());
    };

    syncPermission();
    window.addEventListener('focus', syncPermission);

    return () => {
      window.removeEventListener('focus', syncPermission);
    };
  }, []);

  useEffect(() => {
    dopplerUserApiClient.getAccountNotificationSettings().then((response) => {
      if (response?.success) {
        setAccountNotificationSettings(response.value);
      }
    });
  }, [dopplerUserApiClient]);

  const _ = (id, values) => intl.formatMessage({ id }, values);

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    const response = await dopplerUserApiClient.updateAccountNotificationSettings({
      weeklyAccountReportEnabled: values.weekly_account_report_enabled,
      conversationsMessageNotificationsEnabled: values.conversations_message_notifications_enabled,
      conversationsMessageNotificationSoundEnabled:
        values.conversations_message_notification_sound_enabled,
    });

    setErrors({
      [response.success ? '_success' : '_error']: response.success
        ? 'contact_policy.success_msg'
        : 'common.something_wrong',
    });
    if (response.success) {
      sessionManager.refresh();
    }
    setSubmitting(false);
  };

  const formikConfig = {
    enableReinitialize: true,
    initialValues: {
      weekly_account_report_enabled: accountNotificationSettings?.weeklyAccountReportEnabled,
      conversations_message_notifications_enabled:
        accountNotificationSettings?.conversationsMessageNotificationsEnabled,
      conversations_message_notification_sound_enabled:
        accountNotificationSettings?.conversationsMessageNotificationsEnabled
          ? accountNotificationSettings.conversationsMessageNotificationSoundEnabled
          : false,
    },
  };

  return (
    <>
      <Helmet title={_('control_panel.account_notifications.title')} />
      <HeaderSection>
        <div className="col-sm-12 col-md-12 col-lg-12">
          <Breadcrumb>
            <BreadcrumbItem href={_('common.control_panel_url')} text={_('common.control_panel')} />
            <BreadcrumbItem text={_('control_panel.account_notifications.title')} />
          </Breadcrumb>
          <h2>{_('control_panel.account_notifications.title')}</h2>
        </div>
      </HeaderSection>

      <section className="dp-container">
        <div className="dp-rowflex">
          <div className="col-sm-6 m-b-36">
            <Formik {...formikConfig} onSubmit={handleSubmit}>
              {({ values, setFieldValue }) => (
                <Form data-testid="account-notifications-form">
                  <ul className="dp-accordion dp-accordion-control-panel">
                    {showAccountActivity && (
                      <CollapsibleSection
                        title={
                          isCollaborator
                            ? _('control_panel.account_notifications.account_activity_title', {
                                owner_email: ownerEmail,
                              })
                            : _('control_panel.account_notifications.account_activity_title_owner')
                        }
                        status={values.weekly_account_report_enabled}
                        isOpen={isAccountActivityOpen}
                        onToggle={() => setIsAccountActivityOpen((current) => !current)}
                      >
                        <div className="dp-text-switch m-t-12">
                          <div className="dp-switch">
                            <Field
                              type="checkbox"
                              id="account-activity-notification"
                              aria-label={_(
                                'control_panel.account_notifications.account_reports_weekly_label',
                              )}
                              name="weekly_account_report_enabled"
                              disabled={!areSettingsLoaded}
                            />
                            <label htmlFor="account-activity-notification">
                              <span />
                            </label>
                          </div>
                          <div className="m-l-12">
                            <p className="m-b-12">
                              <strong>
                                {_(
                                  'control_panel.account_notifications.account_reports_weekly_label',
                                )}
                              </strong>
                            </p>
                            <p className="m-b-12">
                              {_(
                                'control_panel.account_notifications.account_reports_weekly_description',
                              )}
                            </p>
                            <p className="m-b-24">
                              <FormattedMessage
                                id="control_panel.account_notifications.account_reports_weekly_note"
                                values={{
                                  strong: (chunks) => <strong>{chunks}</strong>,
                                }}
                              />
                            </p>
                          </div>
                        </div>
                      </CollapsibleSection>
                    )}
                    {showConversations && (
                      <CollapsibleSection
                        title={_('control_panel.account_notifications.conversations_title')}
                        status={
                          values.conversations_message_notifications_enabled &&
                          canUseBrowserNotifications(browserNotificationPermission)
                        }
                        isOpen={isConversationsOpen}
                        onToggle={() => setIsConversationsOpen((current) => !current)}
                      >
                        <div className="dp-text-switch m-t-12">
                          <div className="dp-switch">
                            <Field
                              type="checkbox"
                              id="account-notifications-messages"
                              aria-label={_('control_panel.account_notifications.messages_label')}
                              name="conversations_message_notifications_enabled"
                              disabled={!areSettingsLoaded}
                              checked={Boolean(
                                values.conversations_message_notifications_enabled &&
                                canUseBrowserNotifications(browserNotificationPermission),
                              )}
                              onChange={async (event) => {
                                const isEnabled = event.target.checked;
                                if (isEnabled) {
                                  if (await canEnableConversationNotifications()) {
                                    setBrowserNotificationPermission(
                                      BROWSER_NOTIFICATION_PERMISSION.GRANTED,
                                    );
                                    setFieldValue(
                                      'conversations_message_notifications_enabled',
                                      true,
                                    );
                                  }
                                  return;
                                }

                                setFieldValue('conversations_message_notifications_enabled', false);
                                setFieldValue(
                                  'conversations_message_notification_sound_enabled',
                                  false,
                                );
                              }}
                            />
                            <label htmlFor="account-notifications-messages">
                              <span />
                            </label>
                          </div>
                          <div className="m-l-12">
                            <p className="m-b-12">
                              <strong>
                                {_('control_panel.account_notifications.messages_label')}
                              </strong>
                            </p>
                            <p className="m-b-12">
                              {_('control_panel.account_notifications.messages_description')}
                            </p>
                            <p className="m-b-0">
                              <FormattedMessage
                                id="control_panel.account_notifications.messages_note"
                                values={{
                                  strong: (chunks) => <strong>{chunks}</strong>,
                                }}
                              />
                            </p>
                          </div>
                        </div>
                        <div className="dp-text-switch m-t-12">
                          <div className="dp-switch">
                            <Field
                              type="checkbox"
                              id="account-notifications-sound"
                              aria-label={_('control_panel.account_notifications.sound_label')}
                              name="conversations_message_notification_sound_enabled"
                              checked={Boolean(
                                values.conversations_message_notifications_enabled &&
                                canUseBrowserNotifications(browserNotificationPermission) &&
                                values.conversations_message_notification_sound_enabled,
                              )}
                              disabled={
                                !areSettingsLoaded ||
                                !values.conversations_message_notifications_enabled ||
                                !canUseBrowserNotifications(browserNotificationPermission)
                              }
                            />
                            <label
                              htmlFor="account-notifications-sound"
                              aria-disabled={
                                !areSettingsLoaded ||
                                !values.conversations_message_notifications_enabled ||
                                !canUseBrowserNotifications(browserNotificationPermission)
                              }
                            >
                              <span />
                            </label>
                          </div>
                          <div className="m-l-12">
                            <p className="m-b-12">
                              <strong>
                                {_('control_panel.account_notifications.sound_label')}
                              </strong>
                            </p>
                            <p className="m-b-0">
                              {_('control_panel.account_notifications.sound_description')}
                            </p>
                          </div>
                        </div>
                      </CollapsibleSection>
                    )}
                  </ul>
                  <FormMessages />
                  <ul className="dp-group-buttons">
                    <li>
                      <GoBackButton />
                    </li>
                    <li>
                      <SubmitButton disabled={!areSettingsLoaded}>{_('common.save')}</SubmitButton>
                    </li>
                  </ul>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </section>
    </>
  );
});
